import React from "react";
import { useI18n } from "@/i18n/I18nContext";
import { IMAGES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { RESTAURANTS, PIZZERIAS, RESTAURANTS_SECTION } from "@/data/restaurants";
import { Utensils, Pizza, Trees, Download } from "lucide-react";
import jsPDF from "jspdf";

const JUNGLE = {
  it: { title: "Jungle Raider Park", subtitle: "Avventura sugli alberi per grandi e piccoli", desc: "Sopra Bellagio, immerso nella natura, il Jungle Raider Park offre percorsi acrobatici tra gli alberi, carrucole panoramiche e ponti tibetani. Diversi livelli di difficoltà adatti sia ad adulti sia a bambini: una giornata di adrenalina e divertimento in famiglia, a pochi minuti dal centro." },
  en: { title: "Jungle Raider Park", subtitle: "Treetop adventure for kids and adults", desc: "Up in the hills above Bellagio, the Jungle Raider Park offers acrobatic tree-top trails, panoramic zip-lines and rope bridges. Multiple difficulty levels suitable for both adults and children — a day of adrenaline and family fun, minutes from the centre." },
  fr: { title: "Jungle Raider Park", subtitle: "Aventure dans les arbres pour petits et grands", desc: "Au-dessus de Bellagio, en pleine nature, le Jungle Raider Park propose parcours acrobatiques entre les arbres, tyroliennes panoramiques et ponts de singe. Plusieurs niveaux de difficulté pour adultes et enfants — une journée d'adrénaline et de plaisir en famille." }
};

const DOWNLOAD_LABEL = { it: "Scarica la guida PDF", en: "Download PDF guide", fr: "Télécharger le guide PDF" };

const downloadRestaurantsPDF = (lang) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const M = 50, W = 595 - M * 2;
  let y = M;
  const S = RESTAURANTS_SECTION;
  doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.text(S.title[lang] || S.title.it, M, y); y += 28;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  const intro = doc.splitTextToSize(S.intro[lang] || S.intro.it, W);
  doc.text(intro, M, y); y += intro.length * 13 + 16;
  doc.setTextColor(0);
  const writeItem = (title, body, meta) => {
    if (y > 760) { doc.addPage(); y = M; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text(title, M, y); y += 16;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(60);
    const lines = doc.splitTextToSize(body, W);
    doc.text(lines, M, y); y += lines.length * 12 + 4;
    if (meta) {
      doc.setTextColor(120); doc.setFontSize(9);
      const ml = doc.splitTextToSize(meta, W);
      doc.text(ml, M, y); y += ml.length * 11;
    }
    doc.setTextColor(0); y += 12;
  };
  RESTAURANTS.forEach(r => {
    const meta = `${S.closedLabel[lang] || S.closedLabel.it}: ${r.closed[lang] || r.closed.it}${r.note ? "  ·  " + (S.noteLabel[lang] || S.noteLabel.it) + ": " + (r.note[lang] || r.note.it) : ""}`;
    writeItem(r.name, r.desc[lang] || r.desc.it, meta);
  });
  if (y > 720) { doc.addPage(); y = M; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text(S.pizzeriasTitle[lang] || S.pizzeriasTitle.it, M, y); y += 20;
  PIZZERIAS.forEach(p => writeItem(p.name, p.desc[lang] || p.desc.it));
  doc.setFontSize(8); doc.setTextColor(140);
  doc.text("I Lake It Bellagio · ilakeitbellagio.it", M, 820);
  doc.save(`I-Lake-It-Bellagio-Restaurants-${lang}.pdf`);
};

const Section = ({ k, image, images, reverse, straightenFirst }) => {
  const { t } = useI18n();
  const list = images || (image ? [image] : []);
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className={`grid md:grid-cols-2 gap-10 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
          {list.length > 0 && (
            <Reveal>
              <div className={list.length > 1 ? "grid grid-cols-1 gap-4" : ""}>
                {list.map((src, i) => {
                  const straighten = straightenFirst && i === 0;
                  return (
                    <div key={i} className="aspect-[4/3] overflow-hidden rounded-2xl">
                      <img src={src} alt={t(`bellagio.sections.${k}.t`)}
                        className={`w-full h-full object-cover ${straighten ? "" : "img-zoom"}`}
                        style={straighten ? { transform: "rotate(-1.3deg) scale(1.07)" } : undefined} />
                    </div>
                  );
                })}
              </div>
            </Reveal>
          )}
          <Reveal delay={120}>
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A] tracking-tight">{t(`bellagio.sections.${k}.t`)}</h2>
              <p className="mt-5 text-base md:text-lg text-[#5C5C5C] leading-relaxed">{t(`bellagio.sections.${k}.d`)}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const TextOnly = ({ items }) => {
  const { t } = useI18n();
  return (
    <section className="py-16 md:py-24 bg-[#F2EFEB]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10">
        {items.map((k, i) => (
          <Reveal key={k} delay={i * 80}>
            <div>
              <h3 className="font-display text-2xl text-[#1A1A1A] mb-3">{t(`bellagio.sections.${k}.t`)}</h3>
              <p className="text-[#5C5C5C] leading-relaxed">{t(`bellagio.sections.${k}.d`)}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const RestaurantsGuide = () => {
  const { lang } = useI18n();
  const S = RESTAURANTS_SECTION;
  return (
    <section data-testid="restaurants-section" className="py-20 md:py-28 bg-[#FAF9F6]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center mb-12">
            <Utensils className="w-8 h-8 text-[#1D3557] mx-auto mb-4" />
            <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">{S.title[lang] || S.title.it}</h2>
            <p className="mt-5 text-[#5C5C5C] max-w-3xl mx-auto leading-relaxed">{S.intro[lang] || S.intro.it}</p>
            <button data-testid="download-restaurants-pdf" onClick={() => downloadRestaurantsPDF(lang)}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1D3557] text-white text-sm font-medium hover:bg-[#345B73] transition-all">
              <Download className="w-4 h-4" /> {DOWNLOAD_LABEL[lang] || DOWNLOAD_LABEL.it}
            </button>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {RESTAURANTS.map((r, i) => (
            <Reveal key={r.id} delay={(i % 4) * 60}>
              <article data-testid={`restaurant-${r.id}`} className="bg-white p-6 md:p-7 rounded-2xl border border-black/5 h-full">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-display text-xl text-[#1A1A1A]"><span aria-hidden className="mr-2">{r.icon}</span>{r.name}</h3>
                </div>
                <p className="text-sm text-[#5C5C5C] leading-relaxed mb-4">{r.desc[lang] || r.desc.it}</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#F2EFEB] text-[#1A1A1A]">
                    <strong className="mr-1.5">{S.closedLabel[lang] || S.closedLabel.it}:</strong> {r.closed[lang] || r.closed.it}
                  </span>
                  {r.note && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#1D3557]/10 text-[#1D3557]">
                      <strong className="mr-1.5">{S.noteLabel[lang] || S.noteLabel.it}:</strong> {r.note[lang] || r.note.it}
                    </span>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="text-center mt-20 mb-10">
            <Pizza className="w-8 h-8 text-[#1D3557] mx-auto mb-4" />
            <h3 className="font-display text-3xl md:text-4xl text-[#1A1A1A]">{S.pizzeriasTitle[lang] || S.pizzeriasTitle.it}</h3>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PIZZERIAS.map((p, i) => (
            <Reveal key={p.name} delay={i * 60}>
              <div className="bg-white p-5 rounded-2xl border border-black/5 h-full">
                <h4 className="font-display text-lg text-[#1A1A1A] mb-2">🍕 {p.name}</h4>
                <p className="text-sm text-[#5C5C5C] leading-relaxed">{p.desc[lang] || p.desc.it}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function BellagioPage() {
  const { t } = useI18n();
  return (
    <>
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero[1]} alt="Bellagio" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#FAF9F6]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-white">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.3em] mb-4 text-white/90">Bellagio · Lago di Como</div>
            <h1 data-testid="bellagio-title" className="font-display text-5xl md:text-6xl tracking-tight">{t("bellagio.title")}</h1>
            <p className="mt-6 max-w-2xl text-lg text-white/90 leading-relaxed">{t("bellagio.intro")}</p>
          </Reveal>
        </div>
      </section>
      <Section k="history" image={IMAGES.bellagio.history} />
      <Section k="pearl" image={IMAGES.bellagio.pearl} reverse />
      <Section k="center" image={IMAGES.bellagio.walks} />
      <Section k="villas" images={IMAGES.bellagio.villas} reverse straightenFirst />
      <TextOnly items={["walks", "beaches"]} />
      <Section k="experiences" image={IMAGES.bellagio.boats} />
      <Section k="food" image={IMAGES.bellagio.food} reverse />
      <RestaurantsGuide />
      <TextOnly items={["boats", "watertaxi", "tips"]} />
    </>
  );
}
